import { createSlice, PayloadAction, createSelector } from "@reduxjs/toolkit";
import {
  FilterState,
  CommunicationPageFilters,
  BaseFilter,
  DurationFilter,
  BudgetRange,
  LeaseRange,
} from "../../components/Filters/types";
import { WORKFLO_NAME } from "../../utils/constants/globalConstants";
import { getDefaultDuration } from "../../utils/constants/durationDefaults";
import { endOfDay, startOfDay } from "date-fns";
import {
  formatBillingStartDate,
  formatBillingEndDate,
} from "../../utils/functions/date-time";

const defaultDuration = getDefaultDuration();

// The analytics board compares against this to tell "the user picked a date range" from "this is
// just the default the page booted with" — the date VALUES can't distinguish them (they're always
// populated), only the named preset can. Exported rather than duplicated as a literal because the
// default is client-flag dependent (see utils/constants/durationDefaults.ts).
export const DEFAULT_DURATION_VALUE = defaultDuration.value;

const initialState: FilterState = {
  // Communications page filters
  communications: {
    mode: {
      value: "all",
      displayValue: "All Channels",
    },
    duration: { ...defaultDuration },
    campaign: {
      value: "",
      displayValue: "",
    },
    budget: {
      value: [],
      displayValue: [],
    },
    destinationCountry: {
      value: [],
      displayValue: [],
    },
    sourceCountry: {
      value: [],
      displayValue: [],
    },
    city: {
      value: [],
      displayValue: [],
    },
    university: {
      value: "",
      displayValue: "",
    },
    tags: {
      value: [],
      displayValue: [],
    },
    roomType: {
      value: [],
      displayValue: [],
    },
    property: {
      value: [],
      displayValue: [],
    },
    utmSources: {
      value: [],
      displayValue: [],
    },
    utmMedium: {
      value: [],
      displayValue: [],
    },
    attributionChannel: {
      value: [],
      displayValue: [],
    },
    standardMedium: {
      value: [],
      displayValue: [],
    },
    standardSource: {
      value: [],
      displayValue: [],
    },
    deviceType: {
      value: [],
      displayValue: [],
    },
    lease: {
      value: [],
      displayValue: [],
    },
    assignedAgents: {
      value: [],
      displayValue: [],
    },
    leadType: {
      value: "",
      displayValue: "",
    },
    viewing: false,
    repeatLead: false,
    botVersion: {
      value: [] as string[],
      displayValue: [] as string[],
    },
    visitorPresence: {
      value: "",
      displayValue: "",
    },
  },
  // Campaign communications page filters
  campaignCommunications: {
    mode: {
      value: "all",
      displayValue: "All Channels",
    },
    campaign: {
      value: "",
      displayValue: "",
    },
    status: {
      value: [],
      displayValue: "",
    },
    outcome: {
      value: "",
      displayValue: "",
    },
  },
  // Campaigns page filters
  campaigns: {
    category: {
      value: "",
      displayValue: "",
    },
    status: {
      value: [],
      displayValue: [],
    },
    user: {
      value: "",
      displayValue: "",
    },
    useCase: {
      value: "",
      displayValue: "",
    },
    branch: {
      value: "",
      displayValue: "",
    },
    startedOn: {
      value: {
        from: "",
        to: "",
      },
      displayValue: {
        from: "",
        to: "",
      },
    },
    createdOn: {
      value: {
        from: "",
        to: "",
      },
      displayValue: {
        from: "",
        to: "",
      },
    },
  },

  // Campaign templates page filters
  campaignTemplates: {
    category: {
      value: "",
      displayValue: "",
    },
    user: {
      value: "",
      displayValue: "",
    },
    useCase: {
      value: "",
      displayValue: "",
    },
    branch: {
      value: "",
      displayValue: "",
    },
    createdOn: {
      value: {
        from: "",
        to: "",
      },
      displayValue: {
        from: "",
        to: "",
      },
    },
  },
  // Campaign logs page filters
  campaignLogs: {
    campaign: {
      value: "",
      displayValue: "",
    },
    user: {
      value: "",
      displayValue: "",
    },
  },
  // Campaign execution logs page filters
  campaignExecutionLogs: {
    mode: {
      value: "all",
      displayValue: "All Channels",
    },
    dateRange: {
      value: {
        from: "",
        to: "",
      },
      displayValue: {
        from: "",
        to: "",
      },
    },
    status: {
      value: "",
      displayValue: "",
    },
  },
  // CRM leads page filters
  crmLeads: {
    duration: { ...defaultDuration },
    communicationType: {
      value: "all",
      displayValue: "All",
    },
    sourceCountry: {
      value: [],
      displayValue: [],
    },
    destinationCountry: {
      value: [],
      displayValue: [],
    },
    destinationCity: {
      value: [],
      displayValue: [],
    },
    university: {
      value: "",
      displayValue: "",
    },
    assignedAgents: {
      value: [],
      displayValue: [],
    },
    tags: {
      value: [],
      displayValue: [],
    },
    roomType: {
      value: [],
      displayValue: [],
    },
    property: {
      value: [],
      displayValue: [],
    },
    utmSources: {
      value: [],
      displayValue: [],
    },
    utmMedium: {
      value: [],
      displayValue: [],
    },
    attributionChannel: {
      value: [],
      displayValue: [],
    },
    standardMedium: {
      value: [],
      displayValue: [],
    },
    standardSource: {
      value: [],
      displayValue: [],
    },
    deviceType: {
      value: [],
      displayValue: [],
    },
    academicYear: {
      value: [],
      displayValue: [],
    },
    leadStage: {
      value: [],
      displayValue: [],
    },
    dealStage: {
      value: [],
      displayValue: [],
    },
    dealPropertyName: {
      value: [],
      displayValue: [],
    },
    dealRoomType: {
      value: [],
      displayValue: [],
    },
    dealAcademicYear: {
      value: [],
      displayValue: [],
    },
    dealAvailabilityTitle: {
      value: [],
      displayValue: [],
    },
    dealCity: {
      value: [],
      displayValue: [],
    },
    dealAssignee: {
      value: [],
      displayValue: [],
    },
    dealRoomNumber: {
      value: "",
      displayValue: "",
    },
    leadType: {
      value: "",
      displayValue: "",
    },
    viewing: false,
    repeatLead: false,
    bedCount: {
      value: "",
      displayValue: "",
    },
    propertyCount: {
      value: "",
      displayValue: "",
    },
    activeFollowUps: false,
    leadsNotUpdated30Days: false,
    lastActivity: {
      value: "",
      displayValue: "",
    },
  },
  // Analytics page filters
  analytics: {
    duration: { ...defaultDuration },
    // Defaults to "all" (user, 2026-08-14) — was "inbound" to mirror the legacy /dashboard
    // endpoints' own default so ClickHouse-migrated tiles agreed with the legacy Mongo-backed
    // charts using the same direction. VerbaIQ board tiles now default to ALL communications
    // instead; a client viewing both the legacy Analytics page and the VerbaIQ board can see
    // different totals for the same period as a result — an accepted tradeoff, not an oversight.
    communicationType: {
      value: "all",
      displayValue: "All",
    },
    modality: {
      value: "all",
      // "All Channels", matching getModalityDisplayValue("all") — which is what setAnalyticsModality
      // writes. "All modalities" here meant the SAME value:"all" state carried two different labels,
      // so clearing the filter silently rewrote the label the user had just picked. Every other
      // modality filter in this slice already uses "All Channels".
      displayValue: "All Channels",
    },
  },
  // Billing page filters
  billing: {
    duration: {
      value: defaultDuration.value,
      displayValue: defaultDuration.displayValue,
      startDate: formatBillingStartDate(startOfDay(new Date(defaultDuration.startDate!))),
      endDate: formatBillingEndDate(endOfDay(new Date(defaultDuration.endDate!))),
    },
    communicationType: {
      value: "inbound",
      displayValue: "Inbound",
    },
  },
  // Playground page filters
  playground: {
    duration: { ...defaultDuration },
  },
  // Playground logs page filters
  playgroundLogs: {
    changesMadeOn: {
      value: "all",
      displayValue: "All",
    },
    createdOn: {
      value: {
        from: "",
        to: "",
      },
      displayValue: {
        from: "",
        to: "",
      },
    },
  },

  // Communication analytics page filters
  communicationAnalytics: {
    mode: {
      value: "widget",
      displayValue: "VerbaChat",
    },
    duration: { ...defaultDuration },
  },
  // CRM inbox page filters
  crmInbox: {
    duration: { ...defaultDuration },
    assignedAgents: {
      value: [],
      displayValue: [],
    },
  },
  // Viewings page filters
  viewings: {
    sort_key: "",
    sort_order: "",
    displayValue: "",
    scheduledOn: {
      value: { from: "", to: "" },
      displayValue: { from: "", to: "" },
    },
    createdOn: {
      value: { from: "", to: "" },
      displayValue: { from: "", to: "" },
    },
    property: {
      value: [] as string[],
      displayValue: [] as string[],
    },
    attendeeResponse: {
      value: [] as string[],
      displayValue: [] as string[],
    },
  },
};

export const filterSlice = createSlice({
  name: "filter",
  initialState,
  reducers: {
    // Communications page actions
    setCommunicationsMode: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.communications.mode.value = action.payload.value;
      state.communications.mode.displayValue =
        action.payload.displayValue || action.payload.value;
    },

    setCommunicationsDuration: (
      state,
      action: PayloadAction<{
        startDate: string;
        endDate: string;
        displayValue?: string;
        value?: string;
      }>
    ) => {
      state.communications.duration.startDate = action.payload.startDate;
      state.communications.duration.endDate = action.payload.endDate;
      state.communications.duration.displayValue =
        action.payload.displayValue || "";
      state.communications.duration.value = action.payload.value || "";
    },

    setCommunicationsCampaign: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.communications.campaign.value = action.payload.value;
      state.communications.campaign.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCommunicationsBudget: (
      state,
      action: PayloadAction<{
        value: BudgetRange[];
        displayValue?: string[];
      }>
    ) => {
      state.communications.budget.value = action.payload.value;
      state.communications.budget.displayValue =
        action.payload.displayValue || [];
    },
    setCommunicationsDestinationCountry: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.communications.destinationCountry.value = action.payload.value;
      state.communications.destinationCountry.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCommunicationsSourceCountry: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.communications.sourceCountry.value = action.payload.value;
      state.communications.sourceCountry.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCommunicationsCity: (
      state,
      action: PayloadAction<{
        value: string | string[];
        displayValue?: string | string[];
      }>
    ) => {
      state.communications.city.value = action.payload.value;
      state.communications.city.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCommunicationsUniversity: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.communications.university.value = action.payload.value;
      state.communications.university.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCommunicationsTags: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.communications.tags.value = action.payload.value;
      state.communications.tags.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCommunicationsRoomType: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.communications.roomType.value = action.payload.value;
      state.communications.roomType.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCommunicationsProperty: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.communications.property.value = action.payload.value;
      state.communications.property.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCommunicationsUtmSources: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.communications.utmSources.value = action.payload.value;
      state.communications.utmSources.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCommunicationsUtmMedium: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.communications.utmMedium.value = action.payload.value;
      state.communications.utmMedium.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCommunicationsLease: (
      state,
      action: PayloadAction<{ value: LeaseRange[]; displayValue?: string[] }>
    ) => {
      state.communications.lease.value = action.payload.value;
      state.communications.lease.displayValue =
        action.payload.displayValue || [];
    },
    setCommunicationsAssignedAgents: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.communications.assignedAgents.value = action.payload.value;
      state.communications.assignedAgents.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCommunicationsLeadType: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.communications.leadType.value = action.payload.value;
      state.communications.leadType.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCommunicationsViewing: (state, action: PayloadAction<boolean>) => {
      state.communications.viewing = action.payload;
    },
    setCommunicationsRepeatLead: (state, action: PayloadAction<boolean>) => {
      state.communications.repeatLead = action.payload;
    },
    setCommunicationsBotVersion: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.communications.botVersion.value = action.payload.value;
      state.communications.botVersion.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCommunicationsVisitorPresence: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.communications.visitorPresence.value = action.payload.value;
      state.communications.visitorPresence.displayValue =
        action.payload.displayValue || "";
    },
    clearCommunicationsProperty: (state) => {
      state.communications.property.value = [];
      state.communications.property.displayValue = [];
    },
    clearCommunicationsUtmSources: (state) => {
      state.communications.utmSources.value = [];
      state.communications.utmSources.displayValue = [];
    },
    clearCommunicationsUtmMedium: (state) => {
      state.communications.utmMedium.value = [];
      state.communications.utmMedium.displayValue = [];
    },
    setCommunicationsAttributionChannel: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.communications.attributionChannel.value = action.payload.value;
      state.communications.attributionChannel.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCommunicationsAttributionChannel: (state) => {
      state.communications.attributionChannel.value = [];
      state.communications.attributionChannel.displayValue = [];
    },
    setCommunicationsStandardMedium: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.communications.standardMedium.value = action.payload.value;
      state.communications.standardMedium.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCommunicationsStandardMedium: (state) => {
      state.communications.standardMedium.value = [];
      state.communications.standardMedium.displayValue = [];
    },
    setCommunicationsStandardSource: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.communications.standardSource.value = action.payload.value;
      state.communications.standardSource.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCommunicationsStandardSource: (state) => {
      state.communications.standardSource.value = [];
      state.communications.standardSource.displayValue = [];
    },
    setCommunicationsDeviceType: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.communications.deviceType.value = action.payload.value;
      state.communications.deviceType.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCommunicationsDeviceType: (state) => {
      state.communications.deviceType.value = [];
      state.communications.deviceType.displayValue = [];
    },
    clearCommunicationsLease: (state) => {
      state.communications.lease.value = [];
      state.communications.lease.displayValue = [];
    },
    clearCommunicationsAssignedAgents: (state) => {
      state.communications.assignedAgents.value = [];
      state.communications.assignedAgents.displayValue = [];
    },
    clearCommunicationsLeadType: (state) => {
      state.communications.leadType.value = "";
      state.communications.leadType.displayValue = "";
    },
    clearCommunicationsViewing: (state) => {
      state.communications.viewing = false;
    },
    clearCommunicationsRepeatLead: (state) => {
      state.communications.repeatLead = false;
    },
    clearCommunicationsTags: (state) => {
      state.communications.tags.value = [];
      state.communications.tags.displayValue = [];
    },
    clearCommunicationsRoomType: (state) => {
      state.communications.roomType.value = [];
      state.communications.roomType.displayValue = [];
    },
    clearCommunicationsUniversity: (state) => {
      state.communications.university.value = "";
      state.communications.university.displayValue = "";
    },
    clearCommunicationsBudget: (state) => {
      state.communications.budget.value = [];
      state.communications.budget.displayValue = [];
    },
    clearCommunicationsDestinationCountry: (state) => {
      state.communications.destinationCountry.value = [];
      state.communications.destinationCountry.displayValue = [];
    },
    clearCommunicationsSourceCountry: (state) => {
      state.communications.sourceCountry.value = [];
      state.communications.sourceCountry.displayValue = [];
    },
    clearCommunicationsCity: (state) => {
      state.communications.city.value = [];
      state.communications.city.displayValue = [];
    },

    clearCommunicationsMode: (state) => {
      state.communications.mode.value = "all";
      state.communications.mode.displayValue = "All Channels";
    },

    clearCommunicationsDuration: (state) => {
      state.communications.duration.startDate = defaultDuration.startDate;
      state.communications.duration.endDate = defaultDuration.endDate;
      state.communications.duration.value = defaultDuration.value;
      state.communications.duration.displayValue = defaultDuration.displayValue;
    },

    clearCommunicationsCampaign: (state) => {
      state.communications.campaign.value = "";
      state.communications.campaign.displayValue = "";
    },
    clearCommunicationsBotVersion: (state) => {
      state.communications.botVersion.value = [];
      state.communications.botVersion.displayValue = [];
    },
    clearCommunicationsVisitorPresence: (state) => {
      state.communications.visitorPresence.value = "";
      state.communications.visitorPresence.displayValue = "";
    },

    clearAllCommunicationsFilters: (state) => {
      state.communications = initialState.communications;
    },

    // Campaign communications page actions
    setCampaignCommunicationsMode: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.campaignCommunications.mode.value = action.payload.value;
      state.campaignCommunications.mode.displayValue =
        action.payload.displayValue || action.payload.value;
    },

    setCampaignCommunicationsCampaign: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.campaignCommunications.campaign.value = action.payload.value;
      state.campaignCommunications.campaign.displayValue =
        action.payload.displayValue || action.payload.value;
    },

    setCampaignCommunicationsCampaignStatus: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string }>
    ) => {
      state.campaignCommunications.status.value = action.payload.value;
      state.campaignCommunications.status.displayValue =
        action.payload.displayValue || action.payload.value.join(",");
    },

    setCampaignCommunicationsOutcome: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.campaignCommunications.outcome.value = action.payload.value;
      state.campaignCommunications.outcome.displayValue =
        action.payload.displayValue || action.payload.value;
    },

    clearCampaignCommunicationsMode: (state) => {
      state.campaignCommunications.mode.value = "all";
      state.campaignCommunications.mode.displayValue = "All Channels";
      state.campaignCommunications.status.value = [];
      state.campaignCommunications.status.displayValue = "";
    },

    clearCampaignCommunicationsCampaign: (state) => {
      state.campaignCommunications.campaign.value = "";
      state.campaignCommunications.campaign.displayValue = "";
    },

    clearCampaignCommunicationsStatus: (state) => {
      state.campaignCommunications.status.value = [];
      state.campaignCommunications.status.displayValue = "";
    },

    clearAllCampaignCommunicationsFilters: (state) => {
      state.campaignCommunications = initialState.campaignCommunications;
    },

    clearCampaignCommunicationsOutcome: (state) => {
      state.campaignCommunications.outcome.value = "";
      state.campaignCommunications.outcome.displayValue = "";
    },

    //Campaigns page actions
    setCampaignsCategory: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.campaigns.category.value = action.payload.value;
      state.campaigns.category.displayValue =
        action.payload.displayValue || action.payload.value;
    },

    setCampaignsStatus: (
      state,
      action: PayloadAction<{
        value: string | string[];
        displayValue?: string | string[];
      }>
    ) => {
      state.campaigns.status.value = action.payload.value;
      state.campaigns.status.displayValue =
        action.payload.displayValue || action.payload.value;
    },

    setCampaignsUser: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.campaigns.user.value = action.payload.value;
      state.campaigns.user.displayValue =
        action.payload.displayValue || action.payload.value;
    },

    setCampaignsUseCase: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.campaigns.useCase.value = action.payload.value;
      state.campaigns.useCase.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCampaignsBranch: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.campaigns.branch.value = action.payload.value;
      state.campaigns.branch.displayValue =
        action.payload.displayValue || action.payload.value;
    },

    setCampaignsStartedOn: (
      state,
      action: PayloadAction<{
        value: { from: string; to: string };
        displayValue?: { from: string; to: string };
      }>
    ) => {
      state.campaigns.startedOn.value = action.payload.value;
      state.campaigns.startedOn.displayValue =
        action.payload.displayValue || action.payload.value;
    },

    setCampaignsCreatedOn: (
      state,
      action: PayloadAction<{
        value: { from: string; to: string };
        displayValue?: { from: string; to: string };
      }>
    ) => {
      state.campaigns.createdOn.value = action.payload.value;
      state.campaigns.createdOn.displayValue =
        action.payload.displayValue || action.payload.value;
    },

    clearCampaignsStatus: (state) => {
      state.campaigns.status.value = [];
      state.campaigns.status.displayValue = [];
    },

    clearCampaignsCategory: (state) => {
      state.campaigns.category.value = "";
      state.campaigns.category.displayValue = "";
    },

    clearCampaignsUser: (state) => {
      state.campaigns.user.value = "";
      state.campaigns.user.displayValue = "";
    },

    clearCampaignsUseCase: (state) => {
      state.campaigns.useCase.value = "";
      state.campaigns.useCase.displayValue = "";
    },

    clearCampaignsBranch: (state) => {
      state.campaigns.branch.value = "";
      state.campaigns.branch.displayValue = "";
    },
    clearCampaignsStartedOn: (state) => {
      state.campaigns.startedOn.value = {
        from: "",
        to: "",
      };
      state.campaigns.startedOn.displayValue = {
        from: "",
        to: "",
      };
    },
    clearCampaignsCreatedOn: (state) => {
      state.campaigns.createdOn.value = {
        from: "",
        to: "",
      };
      state.campaigns.createdOn.displayValue = {
        from: "",
        to: "",
      };
    },
    clearAllCampaignsFilters: (state) => {
      state.campaigns = initialState.campaigns;
    },

    // Campaign templates page actions
    setCampaignTemplatesCategory: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.campaignTemplates.category.value = action.payload.value;
      state.campaignTemplates.category.displayValue =
        action.payload.displayValue || action.payload.value;
    },

    setCampaignTemplatesUser: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.campaignTemplates.user.value = action.payload.value;
      state.campaignTemplates.user.displayValue =
        action.payload.displayValue || action.payload.value;
    },

    setCampaignTemplatesUseCase: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.campaignTemplates.useCase.value = action.payload.value;
      state.campaignTemplates.useCase.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCampaignTemplatesBranch: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.campaignTemplates.branch.value = action.payload.value;
      state.campaignTemplates.branch.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCampaignTemplatesCreatedOn: (
      state,
      action: PayloadAction<{
        value: { from: string; to: string };
        displayValue?: { from: string; to: string };
      }>
    ) => {
      state.campaignTemplates.createdOn.value = action.payload.value;
      state.campaignTemplates.createdOn.displayValue =
        action.payload.displayValue || action.payload.value;
    },

    clearCampaignTemplatesCategory: (state) => {
      state.campaignTemplates.category.value = "";
      state.campaignTemplates.category.displayValue = "";
    },

    clearCampaignTemplatesUser: (state) => {
      state.campaignTemplates.user.value = "";
      state.campaignTemplates.user.displayValue = "";
    },

    clearCampaignTemplatesUseCase: (state) => {
      state.campaignTemplates.useCase.value = "";
      state.campaignTemplates.useCase.displayValue = "";
    },

    clearCampaignTemplatesBranch: (state) => {
      state.campaignTemplates.branch.value = "";
      state.campaignTemplates.branch.displayValue = "";
    },

    clearCampaignTemplatesCreatedOn: (state) => {
      state.campaignTemplates.createdOn.value = {
        from: "",
        to: "",
      };
      state.campaignTemplates.createdOn.displayValue = {
        from: "",
        to: "",
      };
    },
    clearAllCampaignTemplatesFilters: (state) => {
      state.campaignTemplates = initialState.campaignTemplates;
    },

    // Campaign logs page actions
    setCampaignLogsCampaign: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.campaignLogs.campaign.value = action.payload.value;
      state.campaignLogs.campaign.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCampaignLogsUser: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.campaignLogs.user.value = action.payload.value;
      state.campaignLogs.user.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCampaignLogsCampaign: (state) => {
      state.campaignLogs.campaign.value = "";
      state.campaignLogs.campaign.displayValue = "";
    },
    clearCampaignLogsUser: (state) => {
      state.campaignLogs.user.value = "";
      state.campaignLogs.user.displayValue = "";
    },
    clearAllCampaignLogsFilters: (state) => {
      state.campaignLogs = initialState.campaignLogs;
    },

    // Campaign execution logs page actions
    setCampaignExecutionLogsMode: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.campaignExecutionLogs.mode.value = action.payload.value;
      state.campaignExecutionLogs.mode.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCampaignExecutionLogsDateRange: (
      state,
      action: PayloadAction<{
        value: { from: string; to: string };
        displayValue?: { from: string; to: string };
      }>
    ) => {
      state.campaignExecutionLogs.dateRange.value = action.payload.value;
      state.campaignExecutionLogs.dateRange.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    setCampaignExecutionLogsStatus: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.campaignExecutionLogs.status.value = action.payload.value;
      state.campaignExecutionLogs.status.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCampaignExecutionLogsMode: (state) => {
      state.campaignExecutionLogs.mode.value = "all";
      state.campaignExecutionLogs.mode.displayValue = "All Channels";
    },
    clearCampaignExecutionLogsDateRange: (state) => {
      state.campaignExecutionLogs.dateRange.value = {
        from: "",
        to: "",
      };
      state.campaignExecutionLogs.dateRange.displayValue = {
        from: "",
        to: "",
      };
    },
    clearAllCampaignExecutionLogsFilters: (state) => {
      state.campaignExecutionLogs = initialState.campaignExecutionLogs;
    },
    clearCampaignExecutionLogsStatus: (state) => {
      state.campaignExecutionLogs.status.value = "";
      state.campaignExecutionLogs.status.displayValue = "";
    },

    // CRM leads page actions
    setCrmLeadsDuration: (
      state,
      action: PayloadAction<{
        startDate: string;
        endDate: string;
        value: string;
        displayValue?: string;
      }>
    ) => {
      state.crmLeads.duration.startDate = action.payload.startDate;
      state.crmLeads.duration.endDate = action.payload.endDate;
      state.crmLeads.duration.value = action.payload.value;
      state.crmLeads.duration.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsDuration: (state) => {
      state.crmLeads.duration.startDate = defaultDuration.startDate;
      state.crmLeads.duration.endDate = defaultDuration.endDate;
      state.crmLeads.duration.value = defaultDuration.value;
      state.crmLeads.duration.displayValue =
        defaultDuration.displayValue;
    },
    setCrmLeadsCommunicationType: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.crmLeads.communicationType.value = action.payload.value;
      state.crmLeads.communicationType.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsCommunicationType: (state) => {
      state.crmLeads.communicationType.value =
        initialState.crmLeads.communicationType.value;
      state.crmLeads.communicationType.displayValue =
        initialState.crmLeads.communicationType.displayValue;
    },
    setCrmLeadsSourceCountry: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.sourceCountry.value = action.payload.value;
      state.crmLeads.sourceCountry.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsSourceCountry: (state) => {
      state.crmLeads.sourceCountry.value = [];
      state.crmLeads.sourceCountry.displayValue = [];
    },
    setCrmLeadsDestinationCountry: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.destinationCountry.value = action.payload.value;
      state.crmLeads.destinationCountry.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsDestinationCountry: (state) => {
      state.crmLeads.destinationCountry.value = [];
      state.crmLeads.destinationCountry.displayValue = [];
    },
    setCrmLeadsDestinationCity: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.destinationCity.value = action.payload.value;
      state.crmLeads.destinationCity.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsDestinationCity: (state) => {
      state.crmLeads.destinationCity.value = [];
      state.crmLeads.destinationCity.displayValue = [];
    },
    setCrmLeadsUniversity: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.crmLeads.university.value = action.payload.value;
      state.crmLeads.university.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsUniversity: (state) => {
      state.crmLeads.university.value = "";
      state.crmLeads.university.displayValue = "";
    },
    setCrmLeadsAssignedAgents: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.assignedAgents.value = action.payload.value;
      state.crmLeads.assignedAgents.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsAssignedAgents: (state) => {
      state.crmLeads.assignedAgents.value = [];
      state.crmLeads.assignedAgents.displayValue = [];
    },
    setCrmLeadsTags: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.tags.value = action.payload.value;
      state.crmLeads.tags.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsTags: (state) => {
      state.crmLeads.tags.value = [];
      state.crmLeads.tags.displayValue = [];
    },
    setCrmLeadsRoomType: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.roomType.value = action.payload.value;
      state.crmLeads.roomType.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsRoomType: (state) => {
      state.crmLeads.roomType.value = [];
      state.crmLeads.roomType.displayValue = [];
    },
    setCrmLeadsProperty: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.property.value = action.payload.value;
      state.crmLeads.property.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsProperty: (state) => {
      state.crmLeads.property.value = [];
      state.crmLeads.property.displayValue = [];
    },
    setCrmLeadsUtmSources: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.utmSources.value = action.payload.value;
      state.crmLeads.utmSources.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsUtmSources: (state) => {
      state.crmLeads.utmSources.value = [];
      state.crmLeads.utmSources.displayValue = [];
    },
    setCrmLeadsUtmMedium: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.utmMedium.value = action.payload.value;
      state.crmLeads.utmMedium.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsUtmMedium: (state) => {
      state.crmLeads.utmMedium.value = [];
      state.crmLeads.utmMedium.displayValue = [];
    },
    setCrmLeadsAttributionChannel: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.attributionChannel.value = action.payload.value;
      state.crmLeads.attributionChannel.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsAttributionChannel: (state) => {
      state.crmLeads.attributionChannel.value = [];
      state.crmLeads.attributionChannel.displayValue = [];
    },
    setCrmLeadsStandardMedium: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.standardMedium.value = action.payload.value;
      state.crmLeads.standardMedium.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsStandardMedium: (state) => {
      state.crmLeads.standardMedium.value = [];
      state.crmLeads.standardMedium.displayValue = [];
    },
    setCrmLeadsStandardSource: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.standardSource.value = action.payload.value;
      state.crmLeads.standardSource.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsStandardSource: (state) => {
      state.crmLeads.standardSource.value = [];
      state.crmLeads.standardSource.displayValue = [];
    },
    setCrmLeadsDeviceType: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.deviceType.value = action.payload.value;
      state.crmLeads.deviceType.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsDeviceType: (state) => {
      state.crmLeads.deviceType.value = [];
      state.crmLeads.deviceType.displayValue = [];
    },
    setCrmLeadsAcademicYear: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.academicYear.value = action.payload.value;
      state.crmLeads.academicYear.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsAcademicYear: (state) => {
      state.crmLeads.academicYear.value = [];
      state.crmLeads.academicYear.displayValue = [];
    },
    setCrmLeadsLeadStage: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.leadStage.value = action.payload.value;
      state.crmLeads.leadStage.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsLeadStage: (state) => {
      state.crmLeads.leadStage.value = [];
      state.crmLeads.leadStage.displayValue = [];
    },
    setCrmLeadsDealStage: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.dealStage.value = action.payload.value;
      state.crmLeads.dealStage.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsDealStage: (state) => {
      state.crmLeads.dealStage.value = [];
      state.crmLeads.dealStage.displayValue = [];
    },
    setCrmLeadsDealPropertyName: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.dealPropertyName.value = action.payload.value;
      state.crmLeads.dealPropertyName.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsDealPropertyName: (state) => {
      state.crmLeads.dealPropertyName.value = [];
      state.crmLeads.dealPropertyName.displayValue = [];
    },
    setCrmLeadsDealRoomType: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.dealRoomType.value = action.payload.value;
      state.crmLeads.dealRoomType.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsDealRoomType: (state) => {
      state.crmLeads.dealRoomType.value = [];
      state.crmLeads.dealRoomType.displayValue = [];
    },
    setCrmLeadsDealAcademicYear: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.dealAcademicYear.value = action.payload.value;
      state.crmLeads.dealAcademicYear.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsDealAcademicYear: (state) => {
      state.crmLeads.dealAcademicYear.value = [];
      state.crmLeads.dealAcademicYear.displayValue = [];
    },
    setCrmLeadsDealAvailabilityTitle: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.dealAvailabilityTitle.value = action.payload.value;
      state.crmLeads.dealAvailabilityTitle.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsDealAvailabilityTitle: (state) => {
      state.crmLeads.dealAvailabilityTitle.value = [];
      state.crmLeads.dealAvailabilityTitle.displayValue = [];
    },
    setCrmLeadsDealCity: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.dealCity.value = action.payload.value;
      state.crmLeads.dealCity.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsDealCity: (state) => {
      state.crmLeads.dealCity.value = [];
      state.crmLeads.dealCity.displayValue = [];
    },
    setCrmLeadsDealAssignee: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmLeads.dealAssignee.value = action.payload.value;
      state.crmLeads.dealAssignee.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsDealAssignee: (state) => {
      state.crmLeads.dealAssignee.value = [];
      state.crmLeads.dealAssignee.displayValue = [];
    },
    setCrmLeadsDealRoomNumber: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.crmLeads.dealRoomNumber.value = action.payload.value;
      state.crmLeads.dealRoomNumber.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsDealRoomNumber: (state) => {
      state.crmLeads.dealRoomNumber.value = "";
      state.crmLeads.dealRoomNumber.displayValue = "";
    },
    setCrmLeadsLeadType: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.crmLeads.leadType.value = action.payload.value;
      state.crmLeads.leadType.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmLeadsLeadType: (state) => {
      state.crmLeads.leadType.value = "";
      state.crmLeads.leadType.displayValue = "";
    },
    setCrmLeadsViewing: (state, action: PayloadAction<boolean>) => {
      state.crmLeads.viewing = action.payload;
    },
    clearCrmLeadsViewing: (state) => {
      state.crmLeads.viewing = false;
    },
    setCrmLeadsRepeatLead: (state, action: PayloadAction<boolean>) => {
      state.crmLeads.repeatLead = action.payload;
    },
    clearCrmLeadsRepeatLead: (state) => {
      state.crmLeads.repeatLead = false;
    },
    setCrmLeadsBedCount: (
      state,
      action: PayloadAction<{ value: string; displayValue: string }>
    ) => {
      state.crmLeads.bedCount.value = action.payload.value;
      state.crmLeads.bedCount.displayValue = action.payload.displayValue;
    },
    clearCrmLeadsBedCount: (state) => {
      state.crmLeads.bedCount.value = "";
      state.crmLeads.bedCount.displayValue = "";
    },
    setCrmLeadsPropertyCount: (
      state,
      action: PayloadAction<{ value: string; displayValue: string }>
    ) => {
      state.crmLeads.propertyCount.value = action.payload.value;
      state.crmLeads.propertyCount.displayValue = action.payload.displayValue;
    },
    clearCrmLeadsPropertyCount: (state) => {
      state.crmLeads.propertyCount.value = "";
      state.crmLeads.propertyCount.displayValue = "";
    },
    setCrmLeadsActiveFollowUps: (state, action: PayloadAction<boolean>) => {
      state.crmLeads.activeFollowUps = action.payload;
    },
    clearCrmLeadsActiveFollowUps: (state) => {
      state.crmLeads.activeFollowUps = false;
    },
    setCrmLeadsNotUpdated30Days: (state, action: PayloadAction<boolean>) => {
      state.crmLeads.leadsNotUpdated30Days = action.payload;
    },
    clearCrmLeadsNotUpdated30Days: (state) => {
      state.crmLeads.leadsNotUpdated30Days = false;
    },
    setCrmLeadsLastActivity: (
      state,
      action: PayloadAction<{ value: string; displayValue: string }>
    ) => {
      state.crmLeads.lastActivity.value = action.payload.value;
      state.crmLeads.lastActivity.displayValue = action.payload.displayValue;
    },
    clearCrmLeadsLastActivity: (state) => {
      state.crmLeads.lastActivity.value = "";
      state.crmLeads.lastActivity.displayValue = "";
    },
    clearAllCrmLeadsFilters: (state) => {
      state.crmLeads = initialState.crmLeads;
    },

    // Analytics page actions
    setAnalyticsDuration: (
      state,
      action: PayloadAction<{
        startDate: string;
        endDate: string;
        displayValue?: string;
        value?: string;
      }>
    ) => {
      state.analytics.duration.startDate = action.payload.startDate;
      state.analytics.duration.endDate = action.payload.endDate;
      state.analytics.duration.displayValue = action.payload.displayValue || "";
      state.analytics.duration.value = action.payload.value || "";
    },
    clearAnalyticsDuration: (state) => {
      state.analytics.duration.startDate = defaultDuration.startDate;
      state.analytics.duration.endDate = defaultDuration.endDate;
      state.analytics.duration.value = defaultDuration.value;
      state.analytics.duration.displayValue = defaultDuration.displayValue;
    },
    setAnalyticsCommunicationType: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.analytics.communicationType.value = action.payload.value;
      state.analytics.communicationType.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearAnalyticsCommunicationType: (state) => {
      state.analytics.communicationType.value = "all";
      state.analytics.communicationType.displayValue = "All";
    },
    setAnalyticsModality: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.analytics.modality.value = action.payload.value;
      state.analytics.modality.displayValue =
        action.payload.displayValue ?? action.payload.value;
    },
    clearAnalyticsModality: (state) => {
      state.analytics.modality.value = "all";
      state.analytics.modality.displayValue = "All Channels";
    },
    clearAllAnalyticsFilters: (state) => {
      state.analytics = initialState.analytics;
    },
    // Billing page actions
    setBillingDuration: (
      state,
      action: PayloadAction<{
        startDate: string;
        endDate: string;
        displayValue?: string;
        value?: string;
      }>
    ) => {
      state.billing.duration.startDate = action.payload.startDate;
      state.billing.duration.endDate = action.payload.endDate;
      state.billing.duration.displayValue = action.payload.displayValue || "";
      state.billing.duration.value = action.payload.value || "";
    },
    clearBillingDuration: (state) => {
      state.billing.duration = { ...initialState.billing.duration };
    },
    setBillingCommunicationType: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.billing.communicationType.value = action.payload.value;
      state.billing.communicationType.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearBillingCommunicationType: (state) => {
      state.billing.communicationType.value = "inbound";
      state.billing.communicationType.displayValue = "Inbound";
    },
    clearAllBillingFilters: (state) => {
      state.billing = initialState.billing;
    },
    // Playground page actions
    setPlaygroundDuration: (
      state,
      action: PayloadAction<{
        startDate: string;
        endDate: string;
        value: string;
        displayValue?: string;
      }>
    ) => {
      state.playground.duration.startDate = action.payload.startDate;
      state.playground.duration.endDate = action.payload.endDate;
      state.playground.duration.value = action.payload.value;
      state.playground.duration.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearPlaygroundDuration: (state) => {
      // Resets the dates too — leaving them behind kept the previously picked range applied
      // under the default's label.
      state.playground.duration = { ...defaultDuration };
    },
    setPlaygroundChangesMadeOn: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.playgroundLogs.changesMadeOn.value = action.payload.value;
      state.playgroundLogs.changesMadeOn.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearPlaygroundChangesMadeOn: (state) => {
      state.playgroundLogs.changesMadeOn.value = "all";
      state.playgroundLogs.changesMadeOn.displayValue = "All";
    },
    setPlaygroundLogsCreatedOn: (
      state,
      action: PayloadAction<{
        value: { from: string; to: string };
        displayValue?: { from: string; to: string };
      }>
    ) => {
      state.playgroundLogs.createdOn.value = action.payload.value;
      state.playgroundLogs.createdOn.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearPlaygroundLogsCreatedOn: (state) => {
      state.playgroundLogs.createdOn.value = {
        from: "",
        to: "",
      };
      state.playgroundLogs.createdOn.displayValue = {
        from: "",
        to: "",
      };
    },
    clearAllPlaygroundLogsFilters: (state) => {
      state.playgroundLogs = initialState.playgroundLogs;
    },
    // Communication analytics page actions
    setCommunicationAnalyticsMode: (
      state,
      action: PayloadAction<{ value: string; displayValue?: string }>
    ) => {
      state.communicationAnalytics.mode.value = action.payload.value;
      state.communicationAnalytics.mode.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCommunicationAnalyticsMode: (state) => {
      state.communicationAnalytics.mode.value = "widget";
      state.communicationAnalytics.mode.displayValue = "VerbaChat";
    },
    setCommunicationAnalyticsDuration: (
      state,
      action: PayloadAction<{
        startDate: string;
        endDate: string;
        value: string;
        displayValue?: string;
      }>
    ) => {
      state.communicationAnalytics.duration.startDate =
        action.payload.startDate;
      state.communicationAnalytics.duration.endDate = action.payload.endDate;
      state.communicationAnalytics.duration.displayValue =
        action.payload.displayValue || "";
      state.communicationAnalytics.duration.value = action.payload.value || "";
    },
    clearCommunicationAnalyticsDuration: (state) => {
      state.communicationAnalytics.duration.startDate = defaultDuration.startDate;
      state.communicationAnalytics.duration.endDate = defaultDuration.endDate;
      state.communicationAnalytics.duration.value = defaultDuration.value;
      state.communicationAnalytics.duration.displayValue = defaultDuration.displayValue;
    },
    // CRM inbox page actions
    setCrmInboxDuration: (
      state,
      action: PayloadAction<{
        startDate: string;
        endDate: string;
        value: string;
        displayValue?: string;
      }>
    ) => {
      state.crmInbox.duration.startDate = action.payload.startDate;
      state.crmInbox.duration.endDate = action.payload.endDate;
      state.crmInbox.duration.value = action.payload.value;
      state.crmInbox.duration.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmInboxDuration: (state) => {
      state.crmInbox.duration.startDate = defaultDuration.startDate;
      state.crmInbox.duration.endDate = defaultDuration.endDate;
      state.crmInbox.duration.value = defaultDuration.value;
      state.crmInbox.duration.displayValue = defaultDuration.displayValue;
    },
    setCrmInboxAssignedAgents: (
      state,
      action: PayloadAction<{ value: string[]; displayValue?: string[] }>
    ) => {
      state.crmInbox.assignedAgents.value = action.payload.value;
      state.crmInbox.assignedAgents.displayValue =
        action.payload.displayValue || action.payload.value;
    },
    clearCrmInboxAssignedAgents: (state) => {
      state.crmInbox.assignedAgents.value = [];
      state.crmInbox.assignedAgents.displayValue = [];
    },
    clearAllCrmInboxFilters: (state) => {
      state.crmInbox = initialState.crmInbox;
    },

    clearAllCommunicationAnalyticsFilters: (state) => {
      state.communicationAnalytics = initialState.communicationAnalytics;
    },
    // Viewings page actions
    setViewingsSort: (
      state,
      action: PayloadAction<{
        sort_key: FilterState["viewings"]["sort_key"];
        sort_order: FilterState["viewings"]["sort_order"];
        displayValue: string;
      }>
    ) => {
      state.viewings.sort_key = action.payload.sort_key;
      state.viewings.sort_order = action.payload.sort_order;
      state.viewings.displayValue = action.payload.displayValue;
    },
    clearViewingsFilters: (state) => {
      state.viewings = initialState.viewings;
    },
    setViewingsScheduledOn: (
      state,
      action: PayloadAction<{
        value: { from: string; to: string };
        displayValue: { from: string; to: string };
      }>
    ) => {
      state.viewings.scheduledOn = action.payload;
    },
    clearViewingsScheduledOn: (state) => {
      state.viewings.scheduledOn = initialState.viewings.scheduledOn;
    },
    setViewingsCreatedOn: (
      state,
      action: PayloadAction<{
        value: { from: string; to: string };
        displayValue: { from: string; to: string };
      }>
    ) => {
      state.viewings.createdOn = action.payload;
    },
    clearViewingsCreatedOn: (state) => {
      state.viewings.createdOn = initialState.viewings.createdOn;
    },
    setViewingsProperty: (
      state,
      action: PayloadAction<{ value: string[]; displayValue: string[] }>
    ) => {
      state.viewings.property = action.payload;
    },
    clearViewingsProperty: (state) => {
      state.viewings.property = initialState.viewings.property;
    },
    setViewingsAttendeeResponse: (
      state,
      action: PayloadAction<{ value: string[]; displayValue: string[] }>
    ) => {
      state.viewings.attendeeResponse = action.payload;
    },
    clearViewingsAttendeeResponse: (state) => {
      state.viewings.attendeeResponse = initialState.viewings.attendeeResponse;
    },
    // Reset all filters across all pages
    resetAllFilters: () => initialState,
  },
});

// Export actions
export const {
  // Communications page actions
  setCommunicationsMode,
  setCommunicationsDuration,
  setCommunicationsCampaign,
  setCommunicationsBudget,
  setCommunicationsDestinationCountry,
  setCommunicationsSourceCountry,
  setCommunicationsCity,
  setCommunicationsUniversity,
  setCommunicationsTags,
  setCommunicationsRoomType,
  setCommunicationsProperty,
  setCommunicationsUtmSources,
  setCommunicationsUtmMedium,
  setCommunicationsAttributionChannel,
  setCommunicationsStandardMedium,
  setCommunicationsStandardSource,
  setCommunicationsDeviceType,
  setCommunicationsLease,
  setCommunicationsAssignedAgents,
  setCommunicationsLeadType,
  setCommunicationsViewing,
  setCommunicationsRepeatLead,
  clearCommunicationsProperty,
  clearCommunicationsUtmSources,
  clearCommunicationsUtmMedium,
  clearCommunicationsAttributionChannel,
  clearCommunicationsStandardMedium,
  clearCommunicationsStandardSource,
  clearCommunicationsDeviceType,
  clearCommunicationsLease,
  clearCommunicationsAssignedAgents,
  clearCommunicationsLeadType,
  clearCommunicationsViewing,
  clearCommunicationsRepeatLead,
  clearCommunicationsMode,
  clearCommunicationsDestinationCountry,
  clearCommunicationsSourceCountry,
  clearCommunicationsCity,
  clearCommunicationsUniversity,
  clearCommunicationsTags,
  clearCommunicationsRoomType,
  clearCommunicationsDuration,
  clearCommunicationsCampaign,
  clearCommunicationsBudget,
  clearAllCommunicationsFilters,
  setCommunicationsBotVersion,
  clearCommunicationsBotVersion,
  setCommunicationsVisitorPresence,
  clearCommunicationsVisitorPresence,
  // Campaign communications page actions
  setCampaignCommunicationsMode,
  setCampaignCommunicationsCampaign,
  setCampaignCommunicationsCampaignStatus,
  setCampaignCommunicationsOutcome,
  clearCampaignCommunicationsStatus,
  clearCampaignCommunicationsMode,
  clearCampaignCommunicationsCampaign,
  clearCampaignCommunicationsOutcome,
  clearAllCampaignCommunicationsFilters,

  // Campaigns page actions
  setCampaignsCategory,
  setCampaignsStatus,
  setCampaignsUser,
  setCampaignsUseCase,
  setCampaignsBranch,
  setCampaignsStartedOn,
  setCampaignsCreatedOn,
  clearCampaignsStatus,
  clearCampaignsCategory,
  clearCampaignsUser,
  clearCampaignsUseCase,
  clearCampaignsBranch,
  clearCampaignsStartedOn,
  clearCampaignsCreatedOn,
  clearAllCampaignsFilters,

  // Campaign templates page actions
  setCampaignTemplatesCategory,
  setCampaignTemplatesUser,
  setCampaignTemplatesUseCase,
  setCampaignTemplatesBranch,
  setCampaignTemplatesCreatedOn,

  clearCampaignTemplatesCategory,
  clearCampaignTemplatesUser,
  clearCampaignTemplatesUseCase,
  clearCampaignTemplatesBranch,
  clearCampaignTemplatesCreatedOn,
  clearAllCampaignTemplatesFilters,

  // Campaign logs page actions
  setCampaignLogsCampaign,
  setCampaignLogsUser,
  clearCampaignLogsCampaign,
  clearCampaignLogsUser,
  clearAllCampaignLogsFilters,

  // Campaign execution logs page actions
  setCampaignExecutionLogsMode,
  setCampaignExecutionLogsDateRange,
  setCampaignExecutionLogsStatus,
  clearCampaignExecutionLogsStatus,
  clearCampaignExecutionLogsDateRange,
  clearCampaignExecutionLogsMode,
  clearAllCampaignExecutionLogsFilters,

  // CRM leads page actions
  setCrmLeadsDuration,
  setCrmLeadsCommunicationType,
  setCrmLeadsSourceCountry,
  setCrmLeadsDestinationCountry,
  setCrmLeadsDestinationCity,
  setCrmLeadsUniversity,
  setCrmLeadsAssignedAgents,
  setCrmLeadsTags,
  setCrmLeadsRoomType,
  setCrmLeadsLeadType,
  setCrmLeadsViewing,
  setCrmLeadsRepeatLead,
  setCrmLeadsProperty,
  clearCrmLeadsProperty,
  setCrmLeadsUtmSources,
  clearCrmLeadsUtmSources,
  setCrmLeadsUtmMedium,
  clearCrmLeadsUtmMedium,
  setCrmLeadsAttributionChannel,
  clearCrmLeadsAttributionChannel,
  setCrmLeadsStandardMedium,
  clearCrmLeadsStandardMedium,
  setCrmLeadsStandardSource,
  clearCrmLeadsStandardSource,
  setCrmLeadsDeviceType,
  clearCrmLeadsDeviceType,
  setCrmLeadsAcademicYear,
  clearCrmLeadsAcademicYear,
  setCrmLeadsLeadStage,
  clearCrmLeadsLeadStage,
  setCrmLeadsDealStage,
  clearCrmLeadsDealStage,
  setCrmLeadsDealPropertyName,
  clearCrmLeadsDealPropertyName,
  setCrmLeadsDealRoomType,
  clearCrmLeadsDealRoomType,
  setCrmLeadsDealAcademicYear,
  clearCrmLeadsDealAcademicYear,
  setCrmLeadsDealAvailabilityTitle,
  clearCrmLeadsDealAvailabilityTitle,
  setCrmLeadsDealCity,
  clearCrmLeadsDealCity,
  setCrmLeadsDealAssignee,
  clearCrmLeadsDealAssignee,
  setCrmLeadsDealRoomNumber,
  clearCrmLeadsDealRoomNumber,
  clearCrmLeadsDuration,
  clearCrmLeadsCommunicationType,
  clearCrmLeadsLeadType,
  clearCrmLeadsTags,
  clearCrmLeadsAssignedAgents,
  clearCrmLeadsSourceCountry,
  clearCrmLeadsDestinationCountry,
  clearCrmLeadsDestinationCity,
  clearCrmLeadsUniversity,
  clearCrmLeadsRoomType,
  clearCrmLeadsViewing,
  clearCrmLeadsRepeatLead,
  setCrmLeadsBedCount,
  clearCrmLeadsBedCount,
  setCrmLeadsPropertyCount,
  clearCrmLeadsPropertyCount,
  setCrmLeadsActiveFollowUps,
  clearCrmLeadsActiveFollowUps,
  setCrmLeadsNotUpdated30Days,
  clearCrmLeadsNotUpdated30Days,
  setCrmLeadsLastActivity,
  clearCrmLeadsLastActivity,
  clearAllCrmLeadsFilters,

  // Analytics page actions
  setAnalyticsDuration,
  clearAnalyticsDuration,
  setAnalyticsCommunicationType,
  clearAnalyticsCommunicationType,
  setAnalyticsModality,
  clearAnalyticsModality,
  clearAllAnalyticsFilters,

  // Billing page actions
  setBillingDuration,
  clearBillingDuration,
  setBillingCommunicationType,
  clearBillingCommunicationType,
  clearAllBillingFilters,

  // Playground page actions
  setPlaygroundDuration,
  clearPlaygroundDuration,
  setPlaygroundChangesMadeOn,
  clearPlaygroundChangesMadeOn,
  setPlaygroundLogsCreatedOn,
  clearPlaygroundLogsCreatedOn,
  clearAllPlaygroundLogsFilters,

  // Communication analytics page actions
  setCommunicationAnalyticsMode,
  setCommunicationAnalyticsDuration,
  clearCommunicationAnalyticsMode,
  clearCommunicationAnalyticsDuration,
  clearAllCommunicationAnalyticsFilters,

  // CRM inbox page actions
  setCrmInboxDuration,
  clearCrmInboxDuration,
  setCrmInboxAssignedAgents,
  clearCrmInboxAssignedAgents,
  clearAllCrmInboxFilters,

  // Viewings page actions
  setViewingsSort,
  clearViewingsFilters,
  setViewingsScheduledOn,
  clearViewingsScheduledOn,
  setViewingsCreatedOn,
  clearViewingsCreatedOn,
  setViewingsProperty,
  clearViewingsProperty,
  setViewingsAttendeeResponse,
  clearViewingsAttendeeResponse,

  // Reset all filters
  resetAllFilters,
} = filterSlice.actions;

// Base selector
export const selectFilterState = (state: { filter: FilterState }) =>
  state.filter;

// Communications page selectors
export const selectCommunicationsFilters = (state: { filter: FilterState }) =>
  state.filter.communications;

export const selectCommunicationsMode = (state: { filter: FilterState }) =>
  state.filter.communications.mode;

export const selectCommunicationsDuration = (state: { filter: FilterState }) =>
  state.filter.communications.duration;

export const selectCommunicationsCampaign = (state: { filter: FilterState }) =>
  state.filter.communications.campaign;

export const selectCommunicationsBudget = (state: { filter: FilterState }) =>
  state.filter.communications.budget;

// Campaign communications page selectors

export const selectCampaignCommunicationsFilters = (state: {
  filter: FilterState;
}) => state.filter.campaignCommunications;

// Campaigns page selectors
export const selectCampaignsFilters = (state: { filter: FilterState }) =>
  state.filter.campaigns;

// Campaign templates page selectors
export const selectCampaignTemplatesFilters = (state: {
  filter: FilterState;
}) => state.filter.campaignTemplates;

// Campaign logs page selectors
export const selectCampaignLogsFilters = (state: { filter: FilterState }) =>
  state.filter.campaignLogs;

// Campaign execution logs page selectors
export const selectCampaignExecutionLogsFilters = (state: {
  filter: FilterState;
}) => state.filter.campaignExecutionLogs;

// CRM leads page selectors
export const selectCrmLeadsFilters = (state: { filter: FilterState }) =>
  state.filter.crmLeads;

// Analytics page selectors
export const selectAnalyticsFilters = (state: { filter: FilterState }) =>
  state.filter.analytics;

// Billing page selectors
export const selectBillingFilters = (state: { filter: FilterState }) =>
  state.filter.billing;

// Computed selectors for communications page
export const selectCommunicationsActiveFilterCount = createSelector(
  [selectCommunicationsFilters],
  (filters) => {
    let count = 0;
    if (filters.mode.value && filters.mode.value !== "all") count++;
    if (filters.duration.value && filters.duration.value !== defaultDuration.value)
      count++;
    if (filters.campaign.value) count++;
    if (filters.destinationCountry.value.length > 0) count++;
    if (filters.sourceCountry.value.length > 0) count++;
    if (filters.city.value.length > 0) count++;
    if (filters.university.value) count++;
    if (filters.tags.value.length > 0) count++;
    if (filters.budget.value.length > 0) count++;
    if (filters.roomType.value.length > 0) count++;
    if (filters.property.value.length > 0) count++;
    if (filters.utmSources.value.length > 0) count++;
    if (filters.utmMedium.value.length > 0) count++;
    if (filters.attributionChannel.value.length > 0) count++;
    if (filters.standardMedium.value.length > 0) count++;
    if (filters.standardSource.value.length > 0) count++;
    if (filters.deviceType.value.length > 0) count++;
    if (filters.lease.value.length > 0) count++;
    if (filters.assignedAgents.value.length > 0) count++;
    if (filters.leadType.value) count++;
    if (filters.viewing) count++;
    if (filters.repeatLead) count++;
    if (filters.mode.value === "widget" && filters.botVersion.value.length > 0)
      count++;
    if (filters.mode.value === "widget" && filters.visitorPresence.value)
      count++;

    return count;
  }
);

export const enableClearAllCommunicationsFilters = createSelector(
  [selectCommunicationsFilters],
  (filters) => {
    const isWidget = filters.mode.value === "widget";
    return (
      filters.mode.value !== "all" ||
      filters.duration.value !== defaultDuration.value ||
      filters.campaign.value !== "" ||
      filters.destinationCountry.value.length > 0 ||
      filters.sourceCountry.value.length > 0 ||
      filters.city.value.length > 0 ||
      filters.university.value !== "" ||
      filters.tags.value.length > 0 ||
      filters.roomType.value.length > 0 ||
      filters.property.value.length > 0 ||
      filters.utmSources.value.length > 0 ||
      filters.utmMedium.value.length > 0 ||
      filters.attributionChannel.value.length > 0 ||
      filters.standardMedium.value.length > 0 ||
      filters.standardSource.value.length > 0 ||
      filters.deviceType.value.length > 0 ||
      filters.lease.value.length > 0 ||
      filters.budget.value.length > 0 ||
      filters.assignedAgents.value.length > 0 ||
      filters.leadType.value !== "" ||
      filters.viewing !== false ||
      filters.repeatLead !== false ||
      (isWidget && filters.botVersion.value.length > 0) ||
      (isWidget && filters.visitorPresence.value !== "")
    );
  }
);

export const selectCommunicationsHasActiveFilters = createSelector(
  [selectCommunicationsActiveFilterCount],
  (count) => count > 0
);

// campaign communications page selectors

export const selectCampaignCommunicationsActiveFilterCount = createSelector(
  [selectCampaignCommunicationsFilters],
  (filters) => {
    let count = 0;
    if (filters.mode.value && filters.mode.value !== "all") count++;
    if (filters.campaign.value && filters.campaign.value !== "") count++;
    return count;
  }
);

export const enableClearAllCampaignCommunicationsFilters = createSelector(
  [selectCampaignCommunicationsFilters],
  (filters) => {
    return (
      filters.mode.value !== "all" ||
      filters.campaign.value !== "" ||
      filters.status.value.length > 0 ||
      filters.outcome.value !== ""
    );
  }
);

export const selectCampaignCommunicationsHasActiveFilters = createSelector(
  [selectCampaignCommunicationsActiveFilterCount],
  (count) => count > 0
);

// Campaigns page selectors
export const selectCampaignsActiveFilterCount = createSelector(
  [selectCampaignsFilters],
  (filters) => {
    let count = 0;
    if (filters.category.value) count++;
    if (
      Array.isArray(filters.status.value)
        ? filters.status.value.length > 0
        : filters.status.value
    )
      count++;
    if (filters.user.value) count++;
    if (filters.useCase.value) count++;
    if (filters.branch.value) count++;
    if (filters.startedOn.value.from) count++;
    if (filters.createdOn.value.from) count++;

    return count;
  }
);

export const enableClearAllCampaignsFilters = createSelector(
  [selectCampaignsFilters],
  (filters) => {
    return (
      filters.category.value !== "" ||
      (Array.isArray(filters.status.value)
        ? filters.status.value.length > 0
        : filters.status.value !== "") ||
      filters.user.value !== "" ||
      filters.useCase.value !== "" ||
      filters.branch.value !== "" ||
      filters.startedOn.value.from !== "" ||
      filters.createdOn.value.from !== ""
    );
  }
);

// Campaign templates page selectors
export const selectCampaignTemplatesActiveFilterCount = createSelector(
  [(state) => state.filter.campaignTemplates],
  (filters) => {
    let count = 0;
    if (filters.category.value) count++;
    if (filters.user.value) count++;
    if (filters.useCase.value) count++;
    if (filters.branch.value) count++;
    if (filters.createdOn.value.from) count++;

    return count;
  }
);

export const enableClearAllCampaignTemplatesFilters = createSelector(
  [(state) => state.filter.campaignTemplates],
  (filters) => {
    return filters.user.value !== "" || filters.createdOn.value.from !== "";
  }
);

export const selectCampaignTemplatesHasActiveFilters = createSelector(
  [selectCampaignTemplatesActiveFilterCount],
  (count) => count > 0
);

// Campaign logs page selectors
export const selectCampaignLogsActiveFilterCount = createSelector(
  [selectCampaignLogsFilters],
  (filters) => {
    let count = 0;
    if (filters.campaign.value) count++;
    if (filters.user.value) count++;

    return count;
  }
);

export const enableClearAllCampaignLogsFilters = createSelector(
  [selectCampaignLogsFilters],
  (filters) => {
    return filters.campaign.value !== "" || filters.user.value !== "";
  }
);

export const selectCampaignLogsHasActiveFilters = createSelector(
  [selectCampaignLogsActiveFilterCount],
  (count) => count > 0
);

// Campaign execution logs page selectors
export const selectCampaignExecutionLogsActiveFilterCount = createSelector(
  [selectCampaignExecutionLogsFilters],
  (filters) => {
    let count = 0;
    if (filters.mode.value) count++;
    if (filters.dateRange.value.from) count++;
    if (filters.status.value) count++;
    return count;
  }
);

export const enableClearAllCampaignExecutionLogsFilters = createSelector(
  [selectCampaignExecutionLogsFilters],
  (filters) => {
    return (
      filters.mode.value !== "all" ||
      filters.dateRange.value.from !== "" ||
      filters.status.value !== ""
    );
  }
);

export const selectCampaignExecutionLogsHasActiveFilters = createSelector(
  [selectCampaignExecutionLogsActiveFilterCount],
  (count) => count > 0
);

// CRM leads page selectors
export const selectCrmLeadsActiveFilterCount = createSelector(
  [selectCrmLeadsFilters],
  (filters) => {
    let count = 0;
    if (filters.sourceCountry.value.length > 0) count++;
    if (filters.destinationCountry.value.length > 0) count++;
    if (filters.destinationCity.value.length > 0) count++;
    if (filters.university.value) count++;
    if (filters.assignedAgents.value.length > 0) count++;
    if (filters.tags.value.length > 0) count++;
    if (filters.roomType.value.length > 0) count++;
    if (filters.leadType.value) count++;
    if (filters.viewing) count++;
    if (filters.repeatLead) count++;
    if (
      filters.duration.value &&
      filters.duration.value !== defaultDuration.value
    )
      count++;
    if (
      filters.communicationType.value &&
      filters.communicationType.value !== "all"
    )
      count++;
    if (filters.property.value.length > 0) count++;
    if (filters.utmSources.value.length > 0) count++;
    if (filters.utmMedium.value.length > 0) count++;
    if (filters.attributionChannel.value.length > 0) count++;
    if (filters.standardMedium.value.length > 0) count++;
    if (filters.standardSource.value.length > 0) count++;
    if (filters.deviceType.value.length > 0) count++;
    if (filters.academicYear.value.length > 0) count++;
    if (filters.leadStage.value.length > 0) count++;
    if (filters.dealStage.value.length > 0) count++;
    if (filters.dealPropertyName.value.length > 0) count++;
    if (filters.dealRoomType.value.length > 0) count++;
    if (filters.dealAcademicYear.value.length > 0) count++;
    if (filters.dealAvailabilityTitle.value.length > 0) count++;
    if (filters.dealCity.value.length > 0) count++;
    if (filters.dealAssignee.value.length > 0) count++;
    if (filters.dealRoomNumber.value) count++;
    if (filters.bedCount.value) count++;
    if (filters.propertyCount.value) count++;
    if (filters.activeFollowUps) count++;
    if (filters.leadsNotUpdated30Days) count++;
    if (filters.lastActivity?.value) count++;
    return count;
  }
);

export const enableClearAllCrmLeadsFilters = createSelector(
  [selectCrmLeadsFilters],
  (filters) => {
    return (
      filters.sourceCountry.value.length > 0 ||
      filters.destinationCountry.value.length > 0 ||
      filters.destinationCity.value.length > 0 ||
      filters.university.value !== "" ||
      filters.assignedAgents.value.length > 0 ||
      filters.tags.value.length > 0 ||
      filters.roomType.value.length > 0 ||
      filters.leadType.value !== "" ||
      filters.viewing ||
      filters.repeatLead ||
      filters.duration.value !== defaultDuration.value ||
      filters.communicationType.value !== "all" ||
      filters.property.value.length > 0 ||
      filters.utmSources.value.length > 0 ||
      filters.utmMedium.value.length > 0 ||
      filters.attributionChannel.value.length > 0 ||
      filters.standardMedium.value.length > 0 ||
      filters.standardSource.value.length > 0 ||
      filters.deviceType.value.length > 0 ||
      filters.academicYear.value.length > 0 ||
      filters.leadStage.value.length > 0 ||
      filters.dealStage.value.length > 0 ||
      filters.dealPropertyName.value.length > 0 ||
      filters.dealRoomType.value.length > 0 ||
      filters.dealAcademicYear.value.length > 0 ||
      filters.dealAvailabilityTitle.value.length > 0 ||
      filters.dealCity.value.length > 0 ||
      filters.dealAssignee.value.length > 0 ||
      filters.dealRoomNumber.value !== "" ||
      filters.bedCount.value !== "" ||
      filters.propertyCount.value !== "" ||
      filters.activeFollowUps ||
      filters.leadsNotUpdated30Days ||
      !!filters.lastActivity?.value
    );
  }
);

export const selectCrmLeadsHasActiveFilters = createSelector(
  [selectCrmLeadsActiveFilterCount],
  (count) => count > 0
);

// Computed selectors for analytics page
export const selectAnalyticsActiveFilterCount = createSelector(
  [selectAnalyticsFilters],
  (filters) => {
    let count = 0;
    if (filters.duration.value && filters.duration.value !== defaultDuration.value)
      count++;
    if (
      filters.communicationType.value &&
      filters.communicationType.value !== "all"
    )
      count++;
    if (filters.modality.value && filters.modality.value !== "all") count++;
    return count;
  }
);

export const enableClearAllAnalyticsFilters = createSelector(
  [selectAnalyticsActiveFilterCount],
  (count) => count > 0
);

export const selectBillingActiveFilterCount = createSelector(
  [selectBillingFilters],
  (filters) => {
    let count = 0;
    if (filters.duration.value && filters.duration.value !== defaultDuration.value)
      count++;
    if (
      filters.communicationType.value &&
      filters.communicationType.value !== "inbound"
    )
      count++;
    return count;
  }
);

export const enableClearAllBillingFilters = createSelector(
  [selectBillingActiveFilterCount],
  (count) => count > 0
);

// Playground page selectors
export const selectPlaygroundFilters = (state: { filter: FilterState }) =>
  state.filter.playground;

export const selectPlaygroundLogsFilters = (state: { filter: FilterState }) =>
  state.filter.playgroundLogs;

export const selectPlaygroundActiveFilterCount = createSelector(
  [(state) => state.filter.playground],
  (filters) => {
    let count = 0;
    if (filters.duration.value && filters.duration.value !== defaultDuration.value)
      count++;
    return count;
  }
);

export const selectPlaygroundLogsActiveFilterCount = createSelector(
  [selectPlaygroundLogsFilters],
  (filters) => {
    let count = 0;
    if (filters.changesMadeOn.value !== "all") count++;
    if (filters.createdOn.value.from) count++;
    return count;
  }
);

export const enableClearAllPlaygroundLogsFilters = createSelector(
  [selectPlaygroundLogsFilters],
  (filters) =>
    filters.changesMadeOn.value !== "all" ||
    filters.createdOn.value.from !== "" ||
    filters.createdOn.value.to !== ""
);

// Communication analytics page selectors

export const selectCommunicationAnalyticsActiveFilterCount = createSelector(
  [(state) => state.filter.communicationAnalytics],
  (filters) => {
    let count = 0;
    if (filters.mode.value && filters.mode.value !== "widget") count++;
    if (filters.duration.value && filters.duration.value !== defaultDuration.value)
      count++;
    return count;
  }
);

export const enableClearAllCommunicationAnalyticsFilters = createSelector(
  [(state) => state.filter.communicationAnalytics],
  (filters) => {
    return (
      filters.mode.value !== "widget" ||
      filters.duration.value !== defaultDuration.value
    );
  }
);

export const selectCrmInboxActiveFilterCount = createSelector(
  [(state) => state.filter.crmInbox],
  (filters) => {
    let count = 0;
    if (filters.duration.value !== defaultDuration.value) count++;
    if (filters.assignedAgents.value.length > 0) count++;
    return count;
  }
);

export const enableClearAllCrmInboxFilters = createSelector(
  [(state) => state.filter.crmInbox],
  (filters) => {
    return (
      filters.duration.value !== defaultDuration.value ||
      filters.assignedAgents.value.length > 0
    );
  }
);

export const selectCrmInboxHasActiveFilters = createSelector(
  [selectCrmInboxActiveFilterCount],
  (count) => count > 0
);

// Viewings page selectors
export const selectViewingsFilters = (state: { filter: FilterState }) =>
  state.filter.viewings;

export const selectViewingsActiveFilterCount = createSelector(
  [selectViewingsFilters],
  (filters) => {
    let count = 0;
    if (filters?.displayValue) count++;
    if (filters?.scheduledOn?.value?.from || filters?.scheduledOn?.value?.to)
      count++;
    if (filters?.createdOn?.value?.from || filters?.createdOn?.value?.to)
      count++;
    if (filters?.property?.value?.length > 0) count++;
    if (filters?.attendeeResponse?.value?.length > 0) count++;
    return count;
  }
);

export const enableClearAllViewingsFilters = createSelector(
  [selectViewingsFilters],
  (filters) =>
    Boolean(filters?.displayValue) ||
    Boolean(filters?.scheduledOn?.value?.from) ||
    Boolean(filters?.scheduledOn?.value?.to) ||
    Boolean(filters?.createdOn?.value?.from) ||
    Boolean(filters?.createdOn?.value?.to) ||
    (filters?.property?.value?.length ?? 0) > 0 ||
    (filters?.attendeeResponse?.value?.length ?? 0) > 0
);

export type {
  FilterState,
  CommunicationPageFilters,
  BaseFilter,
  DurationFilter,
};

export default filterSlice.reducer;
